/**
 * WebHistoryList — the "Web history" tab of the Mobile Forensics view.
 *
 * A reconstructed browsing timeline: visits newest-first, grouped by day, each
 * row showing the page title, its host, when it was visited and which browser
 * recorded it. Search (over title/url/host) and a per-browser chip filter are
 * this component's own state; both run over the loaded window, matching the
 * conversation list. Read-only evidence — rows do not navigate anywhere.
 */

import { ArrowDownIcon, GlobeIcon } from 'lucide-react'
import { type FC, Fragment, memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FilterChip } from '@/components/ui/filter-chip'
import { SearchInput } from '@/components/ui/search-input'
import type { CommsWebVisit } from '@/services/api/comms'
import { appLabel, bubbleTime, dayLabel } from './comms-format'

interface WebHistoryListProps {
  visits: CommsWebVisit[]
  browsers: string[]
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
}

const VisitRow = memo(function VisitRow({ visit }: { visit: CommsWebVisit }) {
  const { t, i18n } = useTranslation()
  return (
    <div className="flex items-center gap-3 border-border/40 border-b px-3 py-2 max-md:items-start">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <GlobeIcon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground max-md:line-clamp-3 max-md:break-words md:truncate">
          {visit.title || visit.url}
        </p>
        <p className="truncate font-mono text-2xs text-muted-foreground">
          {visit.host ?? visit.url}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        {/* A top site has no timestamp at all — saying so beats an empty slot,
            which reads like a visit whose time failed to parse. */}
        <span className="font-mono text-2xs text-muted-foreground">
          {visit.kind === 'top_site'
            ? t('comms.web.noTime')
            : bubbleTime(visit.visitedAt, i18n.language)}
        </span>
        <div className="flex items-center gap-1">
          {visit.kind === 'top_site' && (
            <Badge variant="secondary" className="h-4 px-1 font-mono text-2xs">
              {t('comms.web.topSite', { rank: (visit.rank ?? 0) + 1 })}
            </Badge>
          )}
          {visit.kind !== 'top_site' && visit.visitCount != null && visit.visitCount > 1 && (
            <Badge variant="secondary" className="h-4 px-1 font-mono text-2xs">
              {t('comms.web.visitCount', { count: visit.visitCount })}
            </Badge>
          )}
          <Badge variant="outline" className="h-4 px-1 text-2xs">
            {appLabel(visit.browser, t)}
          </Badge>
        </div>
      </div>
    </div>
  )
})

const DaySeparator: FC<{ iso: string }> = ({ iso }) => {
  const { i18n } = useTranslation()
  return (
    <div className="bg-muted/40 px-3 py-1 font-medium text-2xs text-muted-foreground uppercase tracking-wide">
      {dayLabel(iso, i18n.language)}
    </div>
  )
}

export const WebHistoryList: FC<WebHistoryListProps> = ({
  visits,
  browsers,
  hasMore,
  loadingMore,
  onLoadMore,
}) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [browser, setBrowser] = useState<string | null>(null)

  const needle = search.trim().toLowerCase()
  const visible = useMemo(
    () =>
      visits.filter(visit => {
        if (browser && visit.browser !== browser) return false
        if (!needle) return true
        return `${visit.title ?? ''} ${visit.url} ${visit.host ?? ''}`
          .toLowerCase()
          .includes(needle)
      }),
    [visits, browser, needle]
  )

  // Day boundaries over the filtered, newest-first window — computed once.
  const rows = useMemo(() => {
    let prevDay = ''
    return visible.map(visit => {
      const day = visit.visitedAt ? new Date(visit.visitedAt).toDateString() : ''
      const newDay = day !== prevDay
      prevDay = day
      return { visit, newDay: newDay && !!visit.visitedAt }
    })
  }, [visible])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 px-3 pt-3 pb-2">
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder={t('comms.web.searchPlaceholder')}
          containerClassName="h-9 min-w-56 flex-1 md:h-8"
        />
      </div>
      {browsers.length > 1 && (
        <div className="flex flex-wrap gap-1 px-3 pb-2">
          <FilterChip
            label={t('comms.list.allApps')}
            count={visits.length}
            active={browser === null}
            onClick={() => setBrowser(null)}
          />
          {browsers.map(b => (
            <FilterChip
              key={b}
              label={appLabel(b, t)}
              count={visits.filter(v => v.browser === b).length}
              active={browser === b}
              onClick={() => setBrowser(b)}
            />
          ))}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {rows.map(({ visit, newDay }) => (
          <Fragment key={visit.visitId}>
            {newDay && visit.visitedAt && <DaySeparator iso={visit.visitedAt} />}
            <VisitRow visit={visit} />
          </Fragment>
        ))}
        {visible.length === 0 && (
          <p className="px-3 py-6 text-center text-muted-foreground text-xs">
            {t('comms.web.empty')}
          </p>
        )}
        {hasMore && (
          <div className="flex justify-center p-3">
            <Button variant="outline" size="sm" onClick={onLoadMore} loading={loadingMore}>
              {!loadingMore && <ArrowDownIcon className="size-3.5" />}
              {t('comms.web.loadMore')}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
